import { WithNativeHTMLProps } from "@kalyzee/kast-web-sdk/web-components";
import { Meeting, MeetingSession, MeetingSessionHighlightingState } from "@kalyzee/kast-web-sdk/websocket";
import React, { useRef } from "react";

import { PlayerContainer, ReactIcons } from "@kalyzee/escaet-ui";
import { getFormattedUserFromMeetingSession } from "../../../helpers/meeting";
import "./MeetingPlayerContainer.css";
import styles from "./MeetingPlayerContainer.module.css";
import { BadBehaviorsIndicator } from "../../behaviors/BadBehaviorsIndicator";

export interface MeetingPlayerStyleContainerProps extends WithNativeHTMLProps<HTMLDivElement> {
  mode: "dashboard" | "secondary_screen" | "dashboard_highlight" | "dashboard_highlight_expert";
  meeting: Meeting;
  session?: MeetingSession;
  children?: React.ReactNode;
  className?: string;
  isPlayingSound?: boolean;
  style?: React.CSSProperties;
}

export interface MeetingPlayerContainerRef {}

const MeetingPlayerStyleContainer = ({ mode, meeting, session, children, className, isPlayingSound, style, nativeProps }: MeetingPlayerStyleContainerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // ---------------------------------------------------------------------- //
  // ---------------------------  UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  // ---------------------------------------------------------------------- //
  // ---------------------------  EFFECTS ----------------------------- //
  // ---------------------------------------------------------------------- //

  // ---------------------------------------------------------------------- //
  // ---------------------------  RENDER ----------------------------- //
  // ---------------------------------------------------------------------- //
  const user = session?.user;
  const { fullname, firstname, lastname } = session ? getFormattedUserFromMeetingSession(session, meeting) : { firstname: "-", lastname: "-", fullname: "-" };

  const classes = [styles.container];
  if (className) classes.push(className);

  const renderMainContainer = (children: React.ReactNode, extraClasses: string[]) => {
    classes.push(...extraClasses);
    return (
      <div {...(nativeProps ?? {})} className={classes.join(" ")} style={style} ref={containerRef}>
        {children}
      </div>
    );
  };

  ////////////////////
  // MODE DASHBOARD //
  ////////////////////
  if (mode === "dashboard") {
    return renderMainContainer(
      <PlayerContainer.Default
        containerStyle={{ height: "100%", width: "100%" }}
        overlay="always"
        footerContent={
          user ? (
            <div className={styles.nameContainer} style={isPlayingSound ? { background: "#0090FFAA" } : undefined}>
              <div className={styles.name}>{fullname}</div>
            </div>
          ) : null
        }
      >
        {children}
      </PlayerContainer.Default>,
      [styles.dashboard, "dashboard"]
    );
  }

  ///////////////////////////
  // MODE SECONDARY SCREEN //
  ///////////////////////////
  if (mode === "secondary_screen") {
    return renderMainContainer(
      <PlayerContainer.Default
        containerStyle={{ height: "100%", width: "100%" }}
        overlay="none"
        footerContent={
          <div className={styles.footer}>
            <div className={styles.nameContainer}>
              <div className={styles.firstname}>{firstname ?? "-"}</div>
              <div className={styles.lastname}>{lastname ?? "-"}</div>
            </div>
            {session && <BadBehaviorsIndicator session={session} />}
          </div>
        }
      >
        {children}
        {session?.highlighting === MeetingSessionHighlightingState.PENDING_REQUEST && (
          <div className={styles.pendingRequestHighlightingContainer}>
            <ReactIcons.FingerGrey width={30} height={30} />
          </div>
        )}
      </PlayerContainer.Default>,
      [styles.secondaryScreen, "secondaryScreen"]
    );
  }

  /////////////////////////
  // DASHBOARD HIGHLIGHT //
  /////////////////////////
  if (mode === "dashboard_highlight" || mode === "dashboard_highlight_expert") {
    const isExpert = mode === "dashboard_highlight_expert";
    return renderMainContainer(
      <PlayerContainer.Default
        containerStyle={{ borderRadius: 2, overflow: "hidden" }}
        overlay={"always"}
        footerContent={
          <div
            style={{
              backgroundColor: isExpert ? "#F05E5E" : isPlayingSound ? "#0090FF" : "#444444",
              padding: "0.5em",
            }}
          >
            <div className={styles.name} style={{ color: "white", fontWeight: 700 }}>
              {fullname}
            </div>
          </div>
        }
      >
        {children}
        {isExpert || (
          <div
            style={{
              position: "absolute",
              top: "0",
              left: "0",
              width: "100%",
              height: "100%",
              border: isPlayingSound ? "5px solid #0090FF" : undefined,
            }}
          />
        )}
      </PlayerContainer.Default>,
      [styles.secondaryScreen, "secondaryScreen"]
    );
  }

  return null;
};

export default MeetingPlayerStyleContainer;
